import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'

import { supabase } from '@/lib/supabase'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import { NEW_SYSTEM_START_DATES_KEY } from '@/hooks/useNewSystemStartDates'

const MIDDLEWARE_URL = import.meta.env.VITE_GHL_MIDDLEWARE_URL || null

interface LocationSettingsRow {
  location_id: string
  crm_automation_enabled: boolean | null
  booked_enabled: boolean | null
  quoted_enabled: boolean | null
  lost_enabled: boolean | null
  won_enabled: boolean | null
  fields_update_enabled: boolean | null
  followup_sms_enabled: boolean | null
  move_to_booked_on_payment_enabled: boolean | null
  mark_lost_grace_period_hours: number | null
  new_system_start_at: string | null
}

interface ToggleState {
  // Master gate: CRM mutations (stage moves, status writes, follow-up
  // SMS, field/monetary writes). Existing locations are backfilled to
  // ON; new installs default OFF and operators opt in here.
  crmAutomation: boolean
  booked: boolean
  quoted: boolean
  lost: boolean
  won: boolean
  fieldsUpdate: boolean
  followupSms: boolean
  // Note: this toggle's null-default is FALSE (preserve current behavior),
  // unlike the others which default ON.
  moveToBookedOnPayment: boolean
  // Hours to wait after a customer decline before flipping status to lost.
  // Mirror of the middleware's `markLostGracePeriodHours` LocationToggles
  // field. NULL in the DB → default 24h. 0 → immediate. > 0 → defer.
  markLostGraceHours: number
  // Hand-over DAY to Booking Sales Automation (`YYYY-MM-DD`, '' = not moved).
  // From this day on (in the location's own time zone) the middleware takes
  // on no new work for the location; only already-scheduled follow-ups run.
  newSystemStartDay: string
}

// DEFAULT_TOGGLES is used for the dialog's local state before the row
// loads. We default crmAutomation to TRUE here because the only time the
// dialog is opened with no row is for legacy or manually-created
// locations — the migration backfilled real OAuth-installed locations.
// The middleware reader's NULL-handling is the authoritative source for
// new-install opt-in default.
const DEFAULT_TOGGLES: ToggleState = {
  crmAutomation: true,
  booked: true,
  quoted: true,
  lost: true,
  won: true,
  fieldsUpdate: true,
  followupSms: true,
  moveToBookedOnPayment: false,
  markLostGraceHours: 24,
  newSystemStartDay: '',
}

/**
 * Upper bound on the grace window. Anything beyond a couple of weeks risks
 * a forgotten opportunity sitting in Quoted indefinitely, which defeats the
 * point of having an automated lost-marking pipeline at all. The DB column
 * (`smallint`) tops out at 32767 anyway, but the UI cap keeps operators
 * within a reasonable range.
 */
const MAX_GRACE_HOURS = 720 // 30 days

function clampGraceHours(raw: number): number {
  if (!Number.isFinite(raw) || raw < 0) return 0
  if (raw > MAX_GRACE_HOURS) return MAX_GRACE_HOURS
  return Math.floor(raw)
}

/** The stored start day as a date-input value (`YYYY-MM-DD`); also trims older timestamp values. */
function toDateInputValue(raw: string | null): string {
  return raw && /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : ''
}

/** Today in the browser's calendar, as `YYYY-MM-DD`. */
function todayLocal(): string {
  const d = new Date()
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function rowToToggles(row: LocationSettingsRow | null): ToggleState {
  if (!row) return DEFAULT_TOGGLES
  return {
    // CRM automation defaults FALSE for fetched rows whose column is NULL.
    // This mirrors the middleware reader: post-migration, only locations
    // installed AFTER the deploy will have NULL here, so they get the
    // opt-in default.
    crmAutomation: row.crm_automation_enabled ?? false,
    booked: row.booked_enabled ?? true,
    quoted: row.quoted_enabled ?? true,
    lost: row.lost_enabled ?? true,
    won: row.won_enabled ?? true,
    fieldsUpdate: row.fields_update_enabled ?? true,
    followupSms: row.followup_sms_enabled ?? true,
    moveToBookedOnPayment: row.move_to_booked_on_payment_enabled ?? false,
    markLostGraceHours: row.mark_lost_grace_period_hours ?? 24,
    newSystemStartDay: toDateInputValue(row.new_system_start_at),
  }
}

interface LocationSettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  locationId: string
  locationName: string | null
}

// Untyped supabase client cast for the new ghl_location_settings table.
// Generated types haven't picked it up until the migration is pushed and
// db:types is run. Once that lands, replace with Tables<'ghl_location_settings'>.
const supabaseAny = supabase as unknown as {
  from: (t: string) => {
    select: (s: string) => {
      eq: (k: string, v: string) => {
        maybeSingle: () => Promise<{ data: LocationSettingsRow | null; error: { message: string } | null }>
      }
    }
    upsert: (
      p: Record<string, unknown>,
      o: { onConflict: string }
    ) => Promise<{ error: { message: string } | null }>
    update: (p: Record<string, unknown>) => {
      eq: (k: string, v: string) => {
        eq: (k2: string, v2: string) => Promise<{ error: { message: string } | null; count: number | null }>
      }
    }
  }
}

export function LocationSettingsDialog({
  open,
  onOpenChange,
  locationId,
  locationName,
}: LocationSettingsDialogProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const queryKey = ['admin', 'ghl-location-settings', locationId]

  const settingsQuery = useQuery({
    queryKey,
    enabled: open && !!locationId,
    queryFn: async () => {
      const { data, error } = await supabaseAny
        .from('ghl_location_settings')
        .select(
          'location_id, crm_automation_enabled, ' +
          'booked_enabled, quoted_enabled, lost_enabled, won_enabled, ' +
          'fields_update_enabled, followup_sms_enabled, move_to_booked_on_payment_enabled, ' +
          'mark_lost_grace_period_hours, new_system_start_at'
        )
        .eq('location_id', locationId)
        .maybeSingle()

      if (error) throw new Error(error.message)
      return data
    },
  })

  const [toggles, setToggles] = useState<ToggleState>(DEFAULT_TOGGLES)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Reset local state from server when the dialog opens or the row arrives.
  useEffect(() => {
    if (settingsQuery.data !== undefined) {
      setToggles(rowToToggles(settingsQuery.data))
    }
  }, [settingsQuery.data])

  // Coupling: when Quoted goes off, Follow-up SMS must also go off — the
  // automated follow-up sequence assumes opps live in the Quoted stage.
  // Apply the constraint via setter so the user sees it happen live.
  const setQuoted = (v: boolean) => {
    setToggles(t => ({
      ...t,
      quoted: v,
      // forcibly off when Quoted goes off; preserve current value when on
      followupSms: v ? t.followupSms : false,
    }))
  }

  const followupSmsLocked = !toggles.quoted

  // Track the original DB state so we can detect if Follow-up SMS or the
  // CRM automation master is being turned off as part of this save —
  // either case warrants a confirmation prompt because pending follow-up
  // SMS rows for this location will be bulk-cancelled.
  const original = rowToToggles(settingsQuery.data ?? null)
  const followupGoingOff = original.followupSms && !toggles.followupSms
  const crmAutomationGoingOff = original.crmAutomation && !toggles.crmAutomation
  const willBulkCancelFollowups = followupGoingOff || crmAutomationGoingOff

  // A newly chosen start day of today or earlier takes effect immediately.
  // Allowed (usually deliberate), but called out.
  const startDateChanged = toggles.newSystemStartDay !== original.newSystemStartDay
  const startInPast = startDateChanged && toggles.newSystemStartDay !== '' && toggles.newSystemStartDay <= todayLocal()

  const saveMutation = useMutation({
    mutationFn: async (next: ToggleState) => {
      const payload = {
        location_id: locationId,
        crm_automation_enabled: next.crmAutomation,
        booked_enabled: next.booked,
        quoted_enabled: next.quoted,
        lost_enabled: next.lost,
        won_enabled: next.won,
        fields_update_enabled: next.fieldsUpdate,
        followup_sms_enabled: next.followupSms,
        move_to_booked_on_payment_enabled: next.moveToBookedOnPayment,
        // Coerce any negative or NaN value to 0 (immediate). Cap at a
        // reasonable upper bound so a typo doesn't freeze a deal in the
        // grace window for years.
        mark_lost_grace_period_hours: clampGraceHours(next.markLostGraceHours),
        new_system_start_at: next.newSystemStartDay || null,
      }

      const { error } = await supabaseAny
        .from('ghl_location_settings')
        .upsert(payload, { onConflict: 'location_id' })

      if (error) throw new Error(error.message)

      // If Follow-up SMS just went off OR the CRM automation master just
      // went off, bulk-cancel all pending follow-up schedule rows for this
      // location. The CRM automation gate in the cron will also catch
      // these rows lazily, but operators wanted an immediate, visible
      // action when they flip a master toggle.
      if (willBulkCancelFollowups) {
        const cancelReason = crmAutomationGoingOff
          ? 'crm_automation_disabled_via_settings'
          : 'followup_sms_disabled_via_settings'
        const { error: cancelError } = await supabaseAny
          .from('ghl_followup_schedule')
          .update({
            status: 'cancelled',
            sms_prompt_context: cancelReason,
            last_attempt_at: new Date().toISOString(),
          })
          .eq('location_id', locationId)
          .eq('status', 'pending')

        if (cancelError) {
          // Non-fatal — the lazy cancellation in the cron will still catch
          // these rows when they come due. Surface as a warning.
          toast({
            title: 'Settings saved (with caveat)',
            description:
              `Could not bulk-cancel pending follow-ups: ${cancelError.message}. ` +
              `They will be cancelled individually when their fire times arrive.`,
            variant: 'destructive',
          })
        }
      }

      const invalidateUrls = [MIDDLEWARE_URL].filter(
        (u): u is string => Boolean(u)
      )
      await Promise.allSettled(
        invalidateUrls.map(url =>
          fetch(`${url}/api/internal/invalidate-settings`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ locationId }),
            signal: AbortSignal.timeout(5_000),
          })
        )
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: NEW_SYSTEM_START_DATES_KEY })
      toast({
        title: 'Settings saved',
        description: willBulkCancelFollowups
          ? 'Pending follow-up SMS for this location have been cancelled.'
          : 'New toggles take effect on the next webhook for this location.',
      })
      setConfirmOpen(false)
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to save settings'
      toast({
        title: 'Could not save settings',
        description: message,
        variant: 'destructive',
      })
      setConfirmOpen(false)
    },
  })

  const handleSaveClick = () => {

    if (willBulkCancelFollowups) {
      setConfirmOpen(true)
      return
    }
    saveMutation.mutate(toggles)
  }

  const isLoading = settingsQuery.isLoading
  const isSaving = saveMutation.isPending

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>
              Middleware Settings — {locationName ?? locationId}
            </DialogTitle>
            <DialogDescription>
              Enable or disable middleware behaviors for this GHL location.
              CRM Automation (stage moves, status writes, follow-up SMS, field
              writes) can be toggled here per-location.
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-5 py-2">
              <SectionHeader>Booking Sales Automation</SectionHeader>
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1 flex-1">
                    <Label htmlFor="new-system-start" className="text-sm font-medium">
                      Account start date
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      From this day on, the middleware stops all new work for this location —
                      no analysis, stage or field updates, or new follow-up sequences, for old and
                      new contacts alike. Only follow-ups already scheduled keep sending. The day
                      is counted in this location's time zone (as set in HighLevel). Set the same
                      date in Booking Sales Automation. Leave empty to keep this location fully on
                      the middleware.
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Input
                      id="new-system-start"
                      type="date"
                      value={toggles.newSystemStartDay}
                      onChange={e => setToggles(t => ({ ...t, newSystemStartDay: e.target.value }))}
                      disabled={isSaving}
                      className="w-[170px]"
                    />
                    {toggles.newSystemStartDay && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setToggles(t => ({ ...t, newSystemStartDay: '' }))}
                        disabled={isSaving}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
                {startInPast && (
                  <p className="text-xs text-amber-600">
                    This day has already started, so the middleware stops new work for this
                    location as soon as you save.
                  </p>
                )}
              </div>

              <SectionHeader>Master toggle</SectionHeader>
              <ToggleRow
                id="crm-automation"
                label="CRM Automation"
                description="When off, the middleware performs no CRM mutations — no stage moves, no status writes, no follow-up SMS, no field/monetary updates. The granular sub-toggles below only matter when this is on."
                checked={toggles.crmAutomation}
                onCheckedChange={v => setToggles(t => ({ ...t, crmAutomation: v }))}
                disabled={isSaving}
              />

              {toggles.crmAutomation && (
                <>
                  <SectionHeader>Stages</SectionHeader>
                  <ToggleRow
                    id="booked"
                    label="Booked stage"
                    description="When off, the middleware will not move opportunities to the Booked stage."
                    checked={toggles.booked}
                    onCheckedChange={v => setToggles(t => ({ ...t, booked: v }))}
                    disabled={isSaving}
                  />
                  <ToggleRow
                    id="move-to-booked-on-payment"
                    label="Require payment confirmation to move to Booked"
                    description={
                      toggles.booked
                        ? "When ON, the middleware moves an opportunity to Booked only after Claude detects explicit payment confirmation in the conversation (\"I just paid\", \"Zelle sent\", \"deposit received\"). When OFF, the existing high-confidence rule applies — verbal commitment alone is enough."
                        : "Disabled because Booked stage is off. Re-enable Booked to use this option."
                    }
                    checked={toggles.moveToBookedOnPayment}
                    onCheckedChange={v => setToggles(t => ({ ...t, moveToBookedOnPayment: v }))}
                    disabled={isSaving || !toggles.booked}
                  />
                  <ToggleRow
                    id="quoted"
                    label="Quoted stage"
                    description="When off, the middleware will not auto-advance opportunities from New Lead to Quoted. Follow-up SMS depends on this — turning Quoted off also turns Follow-up SMS off."
                    checked={toggles.quoted}
                    onCheckedChange={setQuoted}
                    disabled={isSaving}
                  />

                  <SectionHeader>Status</SectionHeader>
                  <ToggleRow
                    id="won"
                    label="Won status"
                    description="When off, the middleware will not mark opportunities as won."
                    checked={toggles.won}
                    onCheckedChange={v => setToggles(t => ({ ...t, won: v }))}
                    disabled={isSaving}
                  />
                  <ToggleRow
                    id="lost"
                    label="Lost status"
                    description="When off, the middleware will not mark opportunities as lost."
                    checked={toggles.lost}
                    onCheckedChange={v => setToggles(t => ({ ...t, lost: v }))}
                    disabled={isSaving}
                  />
                  <NumberInputRow
                    id="mark-lost-grace"
                    label="Mark Lost grace period (hours)"
                    description={
                      toggles.lost
                        ? "Wait this many hours after a customer decline before flipping the opportunity to lost. During the window the contact gets the mw-renegotiation-window tag and the deferred lost-write is automatically cancelled if booking intent shows up. Set to 0 to mark lost immediately."
                        : "Disabled because Lost status is off. Re-enable Lost to use the grace window."
                    }
                    value={toggles.markLostGraceHours}
                    min={0}
                    max={MAX_GRACE_HOURS}
                    onChange={v => setToggles(t => ({ ...t, markLostGraceHours: v }))}
                    disabled={isSaving || !toggles.lost}
                  />

                  <SectionHeader>Other</SectionHeader>
                  <ToggleRow
                    id="fields"
                    label="Field updates"
                    description="When off, the middleware will not write opportunity custom fields, contact fields, or monetary value."
                    checked={toggles.fieldsUpdate}
                    onCheckedChange={v => setToggles(t => ({ ...t, fieldsUpdate: v }))}
                    disabled={isSaving}
                  />
                  <ToggleRow
                    id="followup-sms"
                    label="Follow-up SMS"
                    description={
                      followupSmsLocked
                        ? 'Disabled because the Quoted stage is off. Follow-up SMS requires the Quoted stage to be enabled.'
                        : 'When off, no welcome or follow-up SMS messages are sent and follow-up tags are not applied.'
                    }
                    checked={toggles.followupSms}
                    onCheckedChange={v => setToggles(t => ({ ...t, followupSms: v }))}
                    disabled={isSaving || followupSmsLocked}
                  />
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveClick}
              disabled={isLoading || isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel scheduled follow-ups?</AlertDialogTitle>
            <AlertDialogDescription>
              {crmAutomationGoingOff
                ? 'Turning off CRM Automation will immediately cancel every pending follow-up SMS scheduled for this location, in addition to suppressing all stage moves, status writes, and field updates going forward. The cancellations cannot be undone — re-enabling CRM Automation later does not restore them.'
                : 'Turning off Follow-up SMS will immediately cancel every pending follow-up SMS scheduled for this location. The cancellations cannot be undone — re-enabling Follow-up SMS later does not restore them.'}
              {' '}Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Keep current settings</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                saveMutation.mutate(toggles)
              }}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save and cancel pending follow-ups'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  )
}

interface ToggleRowProps {
  id: string
  label: string
  description: string
  checked: boolean
  onCheckedChange: (v: boolean) => void
  disabled: boolean
}

function ToggleRow({
  id,
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
}: ToggleRowProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-1">
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
    </div>
  )
}

interface NumberInputRowProps {
  id: string
  label: string
  description: string
  value: number
  min: number
  max: number
  onChange: (v: number) => void
  disabled: boolean
}

function NumberInputRow({
  id,
  label,
  description,
  value,
  min,
  max,
  onChange,
  disabled,
}: NumberInputRowProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-1 flex-1">
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          // Empty string from a deleted input maps to 0 — operator can re-type.
          const parsed = e.target.value === '' ? 0 : Number(e.target.value)
          onChange(parsed)
        }}
        disabled={disabled}
        className="w-24 shrink-0"
      />
    </div>
  )
}
